import { useEffect, useState } from 'react';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Alert } from '@/components/Alert';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import type { AddTasksToJobInput } from '@maker-toolchest/shared';

interface AddTasksFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddTasksToJobInput) => Promise<void>;
  currentQuantity: number;
  loading?: boolean;
  error?: string | null;
}

export function AddTasksForm({
  open,
  onClose,
  onSubmit,
  currentQuantity,
  loading = false,
  error,
}: AddTasksFormProps) {
  const [additionalQuantity, setAdditionalQuantity] = useState('1');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setAdditionalQuantity('1');
      setErrors({});
    }
  }, [open]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const qty = parseInt(additionalQuantity, 10);
    if (isNaN(qty) || qty < 1) {
      newErrors.additionalQuantity = 'Must add at least 1 task';
    }
    if (qty + currentQuantity > 100) {
      newErrors.additionalQuantity = `Total tasks cannot exceed 100 (current: ${currentQuantity})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await onSubmit({
      additionalQuantity: parseInt(additionalQuantity, 10),
    });
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Add Tasks"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="add-tasks-form" disabled={loading} loading={loading}>
            Add Tasks
          </Button>
        </>
      }
    >
      <form id="add-tasks-form" onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="error" message={error} />
        )}

        <FormSection
            title="Additional Tasks"
            description={`Current tasks: ${currentQuantity}. Add more tasks to this job.`}
          >
            <div>
              <label htmlFor="additionalQuantity" className="block text-sm font-medium text-foreground mb-1">
                Number of Tasks to Add *
              </label>
              <Input
                id="additionalQuantity"
                type="number"
                min="1"
                max={100 - currentQuantity}
                value={additionalQuantity}
                onChange={(e) => {
                  setAdditionalQuantity(e.target.value);
                  if (errors.additionalQuantity) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.additionalQuantity;
                      return next;
                    });
                  }
                }}
                disabled={loading}
                error={errors.additionalQuantity}
              />
              <p className="mt-1 text-xs text-foreground-muted">
                Total after adding: {currentQuantity + (parseInt(additionalQuantity, 10) || 0)}
              </p>
            </div>
          </FormSection>
        </form>
    </SlideOver>
  );
}
